import { useEffect, useRef, useState, createElement as _createElement } from 'react';
import { price as priceUtils } from '@eti/utils';
import { Button, Heading, Stack, Text } from '@etg/wings';
import { useSnowplowTracking } from '@eti/providers';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import { LoadingDots, variants } from '@eti/components';
import { RadioGroup } from '@eti/form';
import isFunction from 'lodash/isFunction';
import { AgeType, Tier } from '@eti/schema-types';
import { formatGet, formatSet, getBundleName, getBundlePosition } from '../../utils/utils';
import BundleModal from '../bundle/BundleModal';
import { BUNDLE_VARIANTS, TRACKING_PROPERTIES, useProductSnowplowTracking } from '../../../custom-hooks/useProductSnowplowTracking';
import ProductBundleItem from './ProductBundleItem';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  ON_PAGE
} = TRACKING_PROPERTIES;
const {
  BUNDLE_YST
} = BUNDLE_VARIANTS;
const wrapperStyles = css`
  border-radius: 3px;
  color: ${etiColors.black};
  height: 100%;
  max-height: 100vh;
  overflow: auto;
`;
const footerStyles = css`
  display: flex;
  flex-direction: column;
  margin-top: 14px;

  @media ${mediaQueries.medium.up} {
    flex-direction: row;
    justify-content: flex-end;
    margin-top: 20px;
  }
`;
const radioGroupStyles = css`
  display: flex;
  margin-top: 16px;
  width: 100%;

  .etiRadioGroup {
    width: 100%;
  }
`;
const optionsWrapperStyles = css`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  @media ${mediaQueries.medium.up} {
    flex-direction: row;
  }
`;
const buttonWrapperStyles = css`
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 10px;

  @media ${mediaQueries.medium.up} {
    background: none;
    margin-left: 8px;
    width: calc(33.33% - 8px);
  }
`;
const buttonStyles = css`
  width: 100%;

  @media ${mediaQueries.medium.up} {
    flex-direction: row;
  }
`;
const marginsBundle = {
  upToMedium: '60px',
  upToLarge: '100px',
  upToXlarge: '120px'
};
const marginInlineStart = margins => {
  return css`
    @media ${mediaQueries.medium.up} {
      margin-inline: ${(margins === null || margins === void 0 ? void 0 : margins.upToMedium) || 0};
    }

    @media ${mediaQueries.large.up} {
      margin-inline: ${(margins === null || margins === void 0 ? void 0 : margins.upToLarge) || 0};
    }

    @media ${mediaQueries.xlarge.up} {
      margin-inline: ${(margins === null || margins === void 0 ? void 0 : margins.upToXlarge) || 0};
    }
  `;
};
const ProductBundle = _ref => {
  var _travelers$filter$len;
  let {
    ancillaryBundleId,
    className,
    confirmButtonText,
    discountPercentagePackageText,
    discountPercentageTitle,
    isLoading,
    isTDOrStepPage,
    name,
    noExtrasText,
    onChange,
    onConfirm,
    options = [],
    priceText,
    productPosition,
    recommendedText,
    selectButtonText,
    selectedValue = {
      id: '',
      price: 0,
      value: Tier.Basic
    },
    shouldShowConfirmationButton = true,
    shouldShowPricePerPerson = false,
    title,
    travelers
  } = _ref;
  const [currentExpanded, setCurrentExpanded] = useState([Tier.Recommended, Tier.Premium]);
  const [selectedBundleToOpen, setSelectedBundleToOpen] = useState(null);
  const rootElement = useRef(null);
  const {
    isFullyVisible,
    sendAddToCartEvent,
    sendClickEvent,
    sendProductSeenEvent,
    updateContext
  } = useProductSnowplowTracking(BUNDLE_YST, {
    rootElement,
    productPosition,
    sendSeenEvent: false
  });
  const bundleName = `productBundle-${name}`;
  const radioGroupName = `${bundleName}-radiogroup`;
  const titles = options.length > 0 ? options.map(option => option.title) : [];
  const {
    isSnowplowTrackingEnabled,
    sendTrackEvent
  } = useSnowplowTracking();
  const numberOfTravelers = (_travelers$filter$len = travelers === null || travelers === void 0 ? void 0 : travelers.filter(traveler => traveler.ageType !== AgeType.Inf).length) !== null && _travelers$filter$len !== void 0 ? _travelers$filter$len : 0;
  useEffect(() => {
    if (isFullyVisible) {
      sendProductSeenEvent({
        property: TRACKING_PROPERTIES.ON_PAGE,
        value: ancillaryBundleId
      });
    }
  }, [ancillaryBundleId, isFullyVisible, sendProductSeenEvent]);
  useEffect(() => {
    const handleSendAddToCartEvent = async () => {
      const isBasicBundle = (selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.value) === 'BASIC';
      await updateContext({
        position: getBundlePosition(options, selectedValue && selectedValue.value),
        quantity: numberOfTravelers,
        price: priceUtils.formatPrice(selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.price)
      });
      sendAddToCartEvent({
        addToCart: !isBasicBundle,
        label: `${BUNDLE_YST}: ${selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.value}`,
        property: TRACKING_PROPERTIES.ON_PAGE,
        value: isBasicBundle ? ancillaryBundleId : selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue.id
      });
    };
    const proceedButton = document.querySelector(`[data-testid='yourSelectedTrip-proceedButton']`);
    if (proceedButton) {
      proceedButton === null || proceedButton === void 0 || proceedButton.addEventListener('click', handleSendAddToCartEvent);
    }
    return () => {
      if (proceedButton) {
        proceedButton.removeEventListener('click', handleSendAddToCartEvent);
      }
    };
  });
  const handleValueChange = async (_event, _ref2) => {
    let {
      price,
      value
    } = _ref2;
    await updateContext({
      position: getBundlePosition(options, value),
      price: priceUtils.formatPrice(price),
      quantity: numberOfTravelers
    });
    sendClickEvent({
      label: `${BUNDLE_YST}: ${value}`,
      property: ON_PAGE,
      value: ancillaryBundleId
    });
    setCurrentExpanded(previous => !previous.includes(value) ? [...previous, value] : previous);
  };
  const toggleItem = type => {
    setCurrentExpanded(previous => previous.includes(type) ? previous.filter(x => x !== type) : [...previous, type]);
  };
  const handleOnConfirm = () => {
    if (isFunction(onConfirm)) {
      onConfirm(selectedValue);
      if (isSnowplowTrackingEnabled) {
        sendTrackEvent({
          category: 'Results',
          action: 'Click',
          label: 'Bundle continue button'
        });
      }
    }
  };
  const handleOpenModal = bundle => () => setSelectedBundleToOpen(bundle);
  const handleCloseModal = () => setSelectedBundleToOpen(null);
  const handleOnChange = (value, field) => {
    if (isFunction(onChange)) {
      onChange(value, field);
    }
  };
  return /*#__PURE__*/_jsxs("section", {
    className: cx(wrapperStyles, className),
    "data-testid": bundleName,
    ref: rootElement,
    children: [/*#__PURE__*/_jsx(BundleModal, {
      isTDOrStepPage: isTDOrStepPage,
      noExtrasText: noExtrasText,
      onChange: handleOnChange,
      onClose: handleCloseModal,
      options: options,
      selectedBundleToOpen: selectedBundleToOpen,
      setSelectedBundleToOpen: setSelectedBundleToOpen,
      shouldShow: Boolean(selectedBundleToOpen),
      titles: titles
    }), /*#__PURE__*/_jsxs(Stack, {
      className: marginInlineStart(!shouldShowConfirmationButton && options.length === 3 ? marginsBundle : null),
      spacing: 16,
      children: [/*#__PURE__*/_jsx(Heading, {
        level: 3,
        children: title
      }), /*#__PURE__*/_jsx(Text, {
        children: discountPercentageTitle
      })]
    }), /*#__PURE__*/_jsx(RadioGroup, {
      className: radioGroupStyles,
      dataTestId: radioGroupName,
      formatGet: formatGet,
      formatSet: formatSet(options),
      name: "value",
      onChange: handleValueChange,
      selectedValue: selectedValue,
      validationRuleName: null,
      children: /*#__PURE__*/_jsx("div", {
        className: optionsWrapperStyles,
        children: options.map(item => {
          const {
            id,
            type
          } = item;
          const bundleOption = getBundleName(item);
          return /*#__PURE__*/_createElement(ProductBundleItem, {
            ...item,
            bundleName: name,
            buttonText: selectButtonText,
            discountPercentagePackageText: discountPercentagePackageText,
            id: id,
            isExpanded: currentExpanded.includes(type),
            isSelected: Boolean(selectedValue && selectedValue.value === type),
            key: id,
            noExtrasText: noExtrasText,
            openInfoModal: handleOpenModal(bundleOption),
            priceText: priceText,
            ribbonText: type === Tier.Recommended ? recommendedText : '',
            shouldShowPricePerPerson: shouldShowPricePerPerson,
            toggleItem: toggleItem,
            totalItems: options.length,
            travelers: travelers,
            type: type
          });
        })
      })
    }), /*#__PURE__*/_jsx("footer", {
      className: footerStyles,
      children: shouldShowConfirmationButton && /*#__PURE__*/_jsx("div", {
        className: buttonWrapperStyles,
        children: /*#__PURE__*/_jsx(Button, {
          className: buttonStyles,
          "data-testid": `${bundleName}-confirmation-button`,
          isDisabled: !selectedValue,
          onClick: handleOnConfirm,
          variant: variants.PRIMARY,
          children: isLoading ? /*#__PURE__*/_jsx(LoadingDots, {}) : confirmButtonText
        })
      })
    })]
  });
};
export default ProductBundle;
//# sourceMappingURL=ProductBundleOverlay.js.map